import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostJson, type PostValue } from './client'

/** Settings → Securitee (settings/Securitee::*), fara familia Antivirus (Bitdefender, lasata). */
export type SecuriteeTab = { id: string; label: string; icon: string; href: string; right: string; title?: string }
export type SecuriteeShell = { pageTitle: string; moduleUrl: string; tabs: SecuriteeTab[] }

export const TABS = ['cryptolocker_protection', 'registry_immunizer', 'files_creation_blocker', 'controlled_folder_access', 'alarms', 'microsoft365', 'gravityzone', 'abuseipdb', 'xdr'] as const
export type SecuriteeTabId = (typeof TABS)[number]

export function isSecuriteeTab(value: string): value is SecuriteeTabId {
  return (TABS as readonly string[]).includes(value)
}

/** Lista fiecarei file, cu numele campului de POST din macheta veche (`extensions[]`, `reg_keys[]`, `files[]`). */
export type ListKind = 'extensions' | 'reg_keys' | 'files'
export const LIST_OF_TAB: Record<Exclude<SecuriteeTabId, 'alarms' | 'microsoft365' | 'gravityzone' | 'abuseipdb' | 'controlled_folder_access' | 'xdr'>, ListKind> = {
  cryptolocker_protection: 'extensions',
  registry_immunizer: 'reg_keys',
  files_creation_blocker: 'files',
}

/** `mode` vine numai la `reg_keys`, si numai dupa migrarea Version20260928120000 (lipsa = report). */
export type RegKeyMode = 'report' | 'revert'
export type ListItem = { ID: number; status: number; extension?: string; key_name?: string; file_name?: string; mode?: RegKeyMode }

export function securiteeShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'securitee', 'shell'],
    queryFn: () => apiGet<SecuriteeShell>('/settings/securitee'),
  })
}

export function securiteeListQuery(kind: ListKind) {
  return queryOptions({
    queryKey: ['settings', 'securitee', 'lists', kind],
    queryFn: () => apiGet<{ items: ListItem[]; canWrite: boolean; moduleUrl: string }>('/settings/securitee/lists/' + kind),
  })
}

export function addListItems(kind: ListKind, values: string[], mode?: RegKeyMode) {
  const body: Record<string, PostValue> = { [kind]: values }
  if (kind === 'reg_keys' && mode) body.mode = mode
  return apiPost<{ ok?: boolean; added?: number }>('/settings/securitee/lists/' + kind, body)
}

/** Registry Immunizer: ce face agentul la o schimbare a cheii. */
export function changeRegKeyMode(id: number, mode: RegKeyMode) {
  return apiPost<{ ok?: boolean; mode?: RegKeyMode }>('/settings/securitee/lists/reg_keys/mode', { id, mode })
}

/**
 * Aceeasi regula ca RegistryKeyName.php: numai HKLM (agentul e serviciu si nu
 * vede cheile omului), spatiile permise. '' = cheia se poate urmari.
 */
export function regKeyProblem(raw: string): string {
  const s = raw.trim().replace(/\//g, '\\').replace(/\\{2,}/g, '\\').replace(/^\\+|\\+$/g, '')
  if (s === '') return 'This field is required.'
  const [root = '', ...rest] = s.split('\\')
  const r = root.toUpperCase()
  if (['HKCU', 'HKEY_CURRENT_USER', 'HKU', 'HKEY_USERS'].includes(r)) return "Only HKLM keys can be watched: the agent runs as a service and does not see the user's own keys."
  if (r !== 'HKLM' && r !== 'HKEY_LOCAL_MACHINE') return 'The key must start with HKLM\\ (for example HKLM\\SOFTWARE\\Microsoft\\Windows\\CurrentVersion\\Run).'
  if (rest.join('\\').trim() === '') return 'The whole HKLM root cannot be watched; name a key under it.'
  return ''
}

export function changeListItemStatus(kind: ListKind, id: number, status: number) {
  return apiPost<{ ok?: boolean }>('/settings/securitee/lists/' + kind + '/status', { id, status })
}

/**
 * Microsoft 365: legatura firmei cu tenantul ei, pentru inventarul
 * atasamentelor trimise (`SettingsSecuriteeM365Controller`).
 */
export type M365State = 'not_configured' | 'not_migrated' | 'disconnected' | 'pending' | 'connected' | 'error'
export type M365Status = {
  configured: boolean
  ready: boolean
  canWrite: boolean
  state: M365State
  tenantId: string | null
  connectedAt: string | null
  lastRunAt: string | null
  lastOkAt: string | null
  lastError: string
  mailboxes: number
  filesLastRun: number
  redirectUri: string
}

export function m365StatusQuery() {
  return queryOptions({
    queryKey: ['settings', 'securitee', 'm365'],
    queryFn: () => apiGet<M365Status>('/settings/securitee/m365'),
  })
}

export function m365Connect() {
  return apiPost<{ url: string }>('/settings/securitee/m365/connect', {})
}

export function m365Disconnect() {
  return apiPost<{ ok?: boolean }>('/settings/securitee/m365/disconnect', {})
}

/**
 * Microsoft 365 > "Service desk actions": acordul tenantului pentru permisiunile
 * biroului AI si opt-in-ul pe fiecare operatie de scriere
 * (`App\Securitee\M365\M365DeskConsent`). `mode`: `same_app` = aceeasi
 * aplicatie ca posta, cu secret (decizia 7, implicit); `certificate` =
 * aplicatia separata "OptimumDesk Desk Actions".
 */
export type M365DeskState = M365State
export type M365DeskRole = { name: string; why: string; granted: boolean }
export type M365DeskOp = { key: string; label: string; roles: string[] }
export type M365DeskStatus = {
  configured: boolean
  ready: boolean
  canWrite: boolean
  state: M365DeskState
  mailConnected: boolean
  consentedAt: string | null
  mode?: 'same_app' | 'certificate'
  clientId?: string
  thumbprint: string
  roles: M365DeskRole[]
  missing: string[]
  writeOps: string[]
  availableOps: M365DeskOp[]
  lastError: string
  redirectUri: string
}

export function m365DeskStatusQuery() {
  return queryOptions({
    queryKey: ['settings', 'securitee', 'm365', 'desk'],
    queryFn: () => apiGet<M365DeskStatus>('/settings/securitee/m365/desk'),
  })
}

export function m365DeskConnect() {
  return apiPost<{ url: string }>('/settings/securitee/m365/desk/connect', {})
}

export function m365DeskRecheck() {
  return apiPost<M365DeskStatus>('/settings/securitee/m365/desk/recheck', {})
}

export function m365DeskSaveOps(ops: string[]) {
  return apiPostJson<{ ok: boolean; writeOps: string[] }>('/settings/securitee/m365/desk/ops', { ops })
}

export function deleteListItem(kind: ListKind, id: number) {
  return apiPost<{ ok?: boolean }>('/settings/securitee/lists/' + kind + '/delete', { id })
}

/**
 * Controlled Folder Access al lui Microsoft Defender (`App\Securitee\Cfa\ControlledFolderAccess`):
 * 0 Off, 1 Audit, 2 Block. `seen` sunt programele pe care CFA le-a oprit
 * (`blocked`, 1123) sau le-ar fi oprit (`audited`, 1124) in ultimele `seenDays` zile.
 */
export type CfaMode = 0 | 1 | 2
export type CfaSeenApp = {
  process: string
  action: 'blocked' | 'audited' | string
  hits: number
  devices: number
  lastSeen: string
  lastFile: string
  allowed: boolean
  canAllow: boolean
}
export type CfaPage = {
  ready: boolean
  canWrite: boolean
  mode: CfaMode
  allowed: string[]
  protected: string[]
  seen: CfaSeenApp[]
  seenDays: number
}

export function cfaQuery() {
  return queryOptions({
    queryKey: ['settings', 'securitee', 'cfa'],
    queryFn: () => apiGet<CfaPage>('/settings/securitee/cfa'),
  })
}

export function saveCfa(mode: CfaMode, allowed: string[], protectedFolders: string[]) {
  return apiPost<CfaPage & { ok: boolean; message: string }>('/settings/securitee/cfa', { mode, allowed, protected: protectedFolders })
}

export function allowCfaApp(path: string) {
  return apiPost<CfaPage & { ok: boolean; message: string }>('/settings/securitee/cfa/allow', { path })
}

/**
 * Lista GLOBALA OptimumDesk de extensii ransomware (`App\Securitee\ExtensionFeed\ExtensionFeed`),
 * tinuta la zi de jobul saptamanal din ph4.api. `global` are cel mult `shownMax`
 * randuri (cele mai noi intai); restul se gasesc cu `search`.
 */
export type ExtensionFeedRow = {
  extension: string
  pattern: string
  source: string
  sourceLabel: string
  sourceUrl: string
  note: string
  firstSeen: string
  lastSeen: string
}
export type ExtensionFeedRun = {
  startedAt: string
  finishedAt: string
  status: 'running' | 'ok' | 'partial' | 'error' | string
  added: number
  removed: number
  proposed: number
  held: number
  aiStatus: string
  errors: string[]
}
export type ExtensionFeedPage = {
  ready: boolean
  canWrite: boolean
  canDecide: boolean
  useGlobal: boolean
  search: string
  total: number
  matching: number
  shownMax: number
  agentMax: number
  global: (ExtensionFeedRow & { excluded: boolean })[]
  excluded: { extension: string; pattern: string; status: string }[]
  proposed: ExtensionFeedRow[]
  lastRun: ExtensionFeedRun | null
}
export type ExtensionFeedReply = ExtensionFeedPage & { ok: boolean; message: string }

export function extensionFeedQuery(search: string) {
  return queryOptions({
    queryKey: ['settings', 'securitee', 'extension_feed', search],
    queryFn: () => apiGet<ExtensionFeedPage>('/settings/securitee/extension_feed' + (search !== '' ? '?q=' + encodeURIComponent(search) : '')),
  })
}

export function setExtensionFeedUseGlobal(on: boolean) {
  return apiPost<ExtensionFeedReply>('/settings/securitee/extension_feed/use_global', { on: on ? 1 : 0 })
}

export type ExtensionFeedAction = 'exclude' | 'include' | 'approve' | 'reject'

export function extensionFeedAction(action: ExtensionFeedAction, extension: string) {
  return apiPost<ExtensionFeedReply>('/settings/securitee/extension_feed/' + action, { extension })
}
